
import { useCurrentRole } from "../../data/hooks/useCurrentRole";
import type { PageKey } from "../../lib/rbac";
import { canAccess } from "../../lib/rbac";
import { useAuthStore } from "../../stores/useAuthStore";
import NotFound from "../shared/NotFoundPage";

interface RequireRoleProps {
    page: PageKey;
    children: React.ReactNode;
}

/**
 * Wraps a route and shows 404 if the current user's role does not have
 * access to the given page. Always used inside RequireAuth so the role
 * is guaranteed to be resolved by the time this renders.
 */
export default function RequireRole({ page, children }: RequireRoleProps) {
    const user = useAuthStore((state) => state.user);
    const role = useCurrentRole();

    // User object not yet resolved — wait before making an access decision.
    // This prevents a flash of 404 while the auth store is still hydrating
    // after login or page refresh.
    if (!user) return null;

    // Deliberately the same 404 shown for a genuinely nonexistent route —
    // an unauthorized user shouldn't be able to tell "doesn't exist" apart
    // from "exists but you can't see it". The copy just needs to not
    // actively mislead by implying the page is simply unbuilt.
    if (!canAccess(page, role)) {
        return (
            <NotFound message="This page doesn't exist, or you don't have permission to view it." />
        );
    }
    return <>{children}</>;
}